import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useOrganization, ORG_CONTEXT_CHANGED_EVENT } from '../../contexts/OrganizationContext';

const MAX_TOKENS = 1_000_000;
const INPUT_PRICE_PER_MILLION_TOKENS = 0.075;
const OUTPUT_PRICE_PER_MILLION_TOKENS = 0.30;
const ESTIMATED_OUTPUT_TOKENS = 1000;

interface EstimatedCosts {
  low: number;
  medium: number;
  high: number;
}

function Chat() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [showPresetQuestions, setShowPresetQuestions] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [totalTokens, setTotalTokens] = useState(0);
  const [tokenError, setTokenError] = useState('');
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectAllDepartments, setSelectAllDepartments] = useState(false);
  const [selectAllUsers, setSelectAllUsers] = useState(false);
  const [showAgentDetails, setShowAgentDetails] = useState(false);
  const [useSemanticSearch, setUseSemanticSearch] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [searchMode, setSearchMode] = useState('high'); // 'low', 'medium', or 'high'
  const [estimatedCosts, setEstimatedCosts] = useState<EstimatedCosts>({
    low: 0,
    medium: 0,
    high: 0
  });
  const { currentContext } = useOrganization();

  useEffect(() => {
    fetchConversations();
    fetchContextSelection();

    // Listen for organization context changes
    const handleOrgContextChange = () => {
      fetchConversations();
      // Clear selected conversation when context changes
      setSelectedConversation(null);
      setMessages([]);
    };

    window.addEventListener(ORG_CONTEXT_CHANGED_EVENT, handleOrgContextChange);

    return () => {
      window.removeEventListener(ORG_CONTEXT_CHANGED_EVENT, handleOrgContextChange);
    };
  }, [currentContext.organization_id]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  // Fetch departments and users for selection
  const fetchContextSelection = async () => {
    try {
      const response = await axios.get('/context-selection');
      setDepartments(response.data.departments || []);
      setUsers(response.data.users || []);
    } catch (error) {
      setDepartments([]);
      setUsers([]);
    }
  };

  // Calculate total tokens for selected items
  useEffect(() => {
    let tokens = 0;
    departments.forEach((dept) => {
      if (selectedDepartments.includes(dept.id)) tokens += dept.token_count || 0;
    });
    users.forEach((user) => {
      if (selectedUsers.includes(user.id)) tokens += user.token_count || 0;
    });
    setTotalTokens(tokens);
    setTokenError(tokens > MAX_TOKENS ? 'Selection exceeds 1 million token context window. Please unselect items.' : '');
  }, [selectedDepartments, selectedUsers, departments, users]);

  // Calculate estimated costs for each search mode
  useEffect(() => {
    const calculateCost = (tokens: number): number => {
      const inputCost = (tokens / 1_000_000) * INPUT_PRICE_PER_MILLION_TOKENS;
      const outputCost = (ESTIMATED_OUTPUT_TOKENS / 1_000_000) * OUTPUT_PRICE_PER_MILLION_TOKENS;
      return Number((inputCost + outputCost).toFixed(4));
    };

    // For low mode, estimate based on a small subset of tokens (most relevant chunks)
    const lowModeTokens = Math.min(totalTokens * 0.1, 50000); // 10% of tokens or 50k max (just chunks)
    
    // For medium mode, estimate based on full documents that contain relevant chunks
    const mediumModeTokens = Math.min(totalTokens * 0.3, 200000); // 30% of tokens or 200k max
    
    // For high mode, use all available tokens (all selected context files)
    const highModeTokens = totalTokens;

    setEstimatedCosts({
      low: calculateCost(lowModeTokens),
      medium: calculateCost(mediumModeTokens),
      high: calculateCost(highModeTokens)
    });
  }, [totalTokens]);

  // Add filtered arrays
  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(departmentSearch.toLowerCase())
  );
  
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Select all departments logic
  useEffect(() => {
    if (selectAllDepartments) {
      setSelectedDepartments(departments.map(dept => dept.id));
    } else if (selectedDepartments.length === departments.length) {
      setSelectedDepartments([]);
    }
    // eslint-disable-next-line
  }, [selectAllDepartments, departments]);

  // Select all users logic
  useEffect(() => {
    if (selectAllUsers) {
      setSelectedUsers(users.map(user => user.id));
    } else if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    }
    // eslint-disable-next-line
  }, [selectAllUsers, users]);

  // Calculate agent breakdown
  const getAgentBreakdown = () => {
    // Group selected departments and users by token count
    const groups = [];
    let currentGroup = { department_ids: [], user_ids: [], tokens: 0 };
    
    // First handle departments
    filteredDepartments.forEach(dept => {
      if (selectedDepartments.includes(dept.id)) {
        if (currentGroup.tokens + dept.token_count > MAX_TOKENS) {
          if (currentGroup.department_ids.length > 0 || currentGroup.user_ids.length > 0) {
            groups.push(currentGroup);
          }
          currentGroup = { department_ids: [dept.id], user_ids: [], tokens: dept.token_count };
        } else {
          currentGroup.department_ids.push(dept.id);
          currentGroup.tokens += dept.token_count;
        }
      }
    });
    
    // Then handle users
    filteredUsers.forEach(user => {
      if (selectedUsers.includes(user.id)) {
        if (currentGroup.tokens + user.token_count > MAX_TOKENS) {
          if (currentGroup.department_ids.length > 0 || currentGroup.user_ids.length > 0) {
            groups.push(currentGroup);
          }
          currentGroup = { department_ids: [], user_ids: [user.id], tokens: user.token_count };
        } else {
          currentGroup.user_ids.push(user.id);
          currentGroup.tokens += user.token_count;
        }
      }
    });
    
    // Add the last group if it has any items
    if (currentGroup.department_ids.length > 0 || currentGroup.user_ids.length > 0) {
      groups.push(currentGroup);
    }
    
    return groups;
  };

  const presetQuestions = [
    'What are my strengths and how can I leverage them?',
    'What are my weaknesses and how can I improve on them?',
    'What brings me happiness and how can I incorporate more of it into my life?',
    'What causes me stress and how can I manage it effectively?',
    'In what moments, environments, and with what audiences do I perform at my best?',
    'What are my highlights and achievements that I am proud of?',
    'What brands do I associate with and why?',
    'How would I describe myself in a few sentences?',
    'What is my personal brand value and how do I communicate it?',
    'What is currently missing in my life that I desire?',
    'How do I define success for myself?',
    'What criteria do I use to measure my success?',
    'What am I truly passionate about?',
    'What do I love and how does it influence my life?',
    'What are my unique talents and how can I utilize them?',
    'What is my profession and how does it align with my values?',
    'What is the demand for my talent in the current market?',
    'What is my mission in life and how do I pursue it?',
    'What is the demand for my love and how do I express it?',
    'What benchmarks do I set for myself to gauge my progress?',
    'What are my life ROI investments and how do they pay off?',
    'What frictions do I encounter in my daily life and how can I resolve them?',
    'What opportunities are available to me and how can I seize them?'
  ];

  const fetchConversations = async () => {
    try {
      const response = await axios.get('/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`/conversations/${conversationId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    if (!selectedConversation) {
      console.error('No conversation selected');
      return;
    }

    const userMessage = { id: Date.now(), content: newMessage, is_user_message: true };
    setMessages([...messages, userMessage]);
    setNewMessage('');

    const loadingMessage = { id: Date.now() + 1, content: 'Loading...', is_user_message: false };
    setMessages(prevMessages => [...prevMessages, loadingMessage]);

    try {
      // Get agent breakdown if total tokens exceed limit
      const agentBreakdown = totalTokens > MAX_TOKENS ? getAgentBreakdown() : null;

      const response = await axios.post(`/conversations/${selectedConversation.id}/messages`, {
        content: newMessage,
        selected_departments: selectedDepartments,
        selected_users: selectedUsers,
        agent_breakdown: agentBreakdown,
        show_agent_details: showAgentDetails,
        search_mode: searchMode
      });
      
      setMessages(prevMessages => [
        ...prevMessages.filter(msg => msg.id !== loadingMessage.id),
        response.data.message
      ]);
      if (response.data.conversation && response.data.conversation.title !== selectedConversation.title) {
        setSelectedConversation(response.data.conversation);
        setConversations(prevConversations => prevConversations.map(conv =>
          conv.id === response.data.conversation.id ? { ...conv, title: response.data.conversation.title } : conv
        ));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== loadingMessage.id));
    }
  };

  const handleNewConversation = async () => {
    try {
      const response = await axios.post('/conversations', {});
      const newConversation = response.data;
      setMessages([]); // Clear any existing messages
      setSelectedConversation(newConversation);
      setConversations(prevConversations => [...prevConversations, newConversation]);
    } catch (error) {
      console.error('Error creating new conversation:', error);
    }
  };

  const handleRenameConversation = async () => {
    if (!newTitle.trim()) return;
    try {
      const response = await axios.put(`/conversations/${selectedConversation.id}`, {
        title: newTitle
      });
      setConversations(conversations.map(conv =>
        conv.id === selectedConversation.id ? { ...conv, title: newTitle } : conv
      ));
      setSelectedConversation({ ...selectedConversation, title: newTitle });
      setIsRenaming(false);
      setNewTitle('');
    } catch (error) {
      console.error('Error renaming conversation:', error);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    try {
      await axios.delete(`/conversations/${conversationId}`);
      setConversations(conversations.filter(conv => conv.id !== conversationId));
      if (selectedConversation && selectedConversation.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handlePresetQuestion = async (question) => {
    setNewMessage(question);
    setShowPresetQuestions(false);
  };

  return (
    <div className="flex flex-col md:flex-row">
      {/* Conversation list */}
      <div className="w-full md:w-1/4 pr-4 mb-4 md:mb-0">
        <button
          onClick={handleNewConversation}
          className="w-full mb-4 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
        >
          New Conversation
        </button>
        <div className="space-y-2">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              className={`p-2 rounded cursor-pointer flex justify-between items-center ${
                selectedConversation && selectedConversation.id === conv.id
                  ? 'bg-blue-100'
                  : 'hover:bg-gray-100'
              }`}
            >
              <div
                className="flex-grow"
                onClick={() => setSelectedConversation(conv)}
              >
                {conv.title || 'Untitled Conversation'}
              </div>
              <button
                onClick={() => handleDeleteConversation(conv.id)}
                className="text-red-500 hover:text-red-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat window */}
      <div className="w-full md:w-3/4 pl-0 md:pl-4">
        {selectedConversation ? (
          <>
            {/* Context selection UI */}
            <div className="mb-4 p-2 bg-gray-50 rounded border border-gray-200">
              <div className="mb-2 font-semibold">Select Departments/Users for Chat Context</div>
              
              {/* Search Mode Selection */}
              <div className="mb-4">
                <div className="font-medium mb-2">Search Mode</div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="searchMode"
                      value="low"
                      checked={searchMode === 'low'}
                      onChange={e => setSearchMode(e.target.value)}
                      className="form-radio"
                    />
                    <span>Low Context</span>
                    <span className="text-xs text-gray-500">(Est. ${estimatedCosts.low})</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="searchMode"
                      value="medium"
                      checked={searchMode === 'medium'}
                      onChange={e => setSearchMode(e.target.value)}
                      className="form-radio"
                    />
                    <span>Medium Context</span>
                    <span className="text-xs text-gray-500">(Est. ${estimatedCosts.medium})</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="searchMode"
                      value="high"
                      checked={searchMode === 'high'}
                      onChange={e => setSearchMode(e.target.value)}
                      className="form-radio"
                    />
                    <span>High Context</span>
                    <span className="text-xs text-gray-500">(Est. ${estimatedCosts.high})</span>
                  </label>
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  {searchMode === 'low' && 'Basic RAG search - uses only the most relevant text chunks for a focused response.'}
                  {searchMode === 'medium' && 'RAG + full documents - finds relevant chunks then uses complete documents for comprehensive context.'}
                  {searchMode === 'high' && 'All selected context - uses all selected documents without RAG filtering for maximum context.'}
                </div>
              </div>

              {/* Agent Settings */}
              {/* <div className="mb-4 flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showAgentDetails}
                    onChange={e => setShowAgentDetails(e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Show Agent Details</span>
                </label>
              </div> */}

              <div className="flex flex-wrap gap-4 mb-2">
                <div>
                  <div className="font-medium mb-1 flex items-center gap-2">
                    Departments
                    <input
                      type="checkbox"
                      checked={selectAllDepartments}
                      onChange={e => setSelectAllDepartments(e.target.checked)}
                      className="ml-2"
                    />
                    <span className="text-xs">Select All</span>
                  </div>
                  <input
                    type="text"
                    placeholder="Search departments..."
                    value={departmentSearch}
                    onChange={e => setDepartmentSearch(e.target.value)}
                    className="mb-1 p-1 border rounded w-full text-sm"
                  />
                  <div className="flex flex-col gap-1 max-h-32 overflow-y-auto">
                    {filteredDepartments.map((dept) => (
                      <label key={dept.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedDepartments.includes(dept.id)}
                          onChange={e => {
                            setSelectedDepartments(prev =>
                              e.target.checked
                                ? [...prev, dept.id]
                                : prev.filter(id => id !== dept.id)
                            );
                            setSelectAllDepartments(false);
                          }}
                        />
                        <span>{dept.name} <span className="text-xs text-gray-500">({dept.token_count.toLocaleString()} tokens)</span></span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="font-medium mb-1 flex items-center gap-2">
                    Users
                    <input
                      type="checkbox"
                      checked={selectAllUsers}
                      onChange={e => setSelectAllUsers(e.target.checked)}
                      className="ml-2"
                    />
                    <span className="text-xs">Select All</span>
                  </div>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    className="mb-1 p-1 border rounded w-full text-sm"
                  />
                  <div className="flex flex-col gap-1 max-h-32 overflow-y-auto">
                    {filteredUsers.map((user) => (
                      <label key={user.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={e => {
                            setSelectedUsers(prev =>
                              e.target.checked
                                ? [...prev, user.id]
                                : prev.filter(id => id !== user.id)
                            );
                            setSelectAllUsers(false);
                          }}
                        />
                        <span>{user.name} <span className="text-xs text-gray-500">({user.token_count.toLocaleString()} tokens)</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-2 font-medium">
                <div>Total tokens selected: <span className={totalTokens > MAX_TOKENS ? 'text-red-600' : 'text-green-700'}>{totalTokens.toLocaleString()}</span> / 1,000,000</div>
              </div>
              
              {/* {totalTokens > MAX_TOKENS && (
                <div className="text-yellow-700 mt-1">
                  Your selection will be split into <b>{getAgentBreakdown().length}</b> agent(s).<br />
                  Breakdown:
                  <ul className="list-disc ml-6">
                    {getAgentBreakdown().map((group, idx) => {
                      const deptNames = group.department_ids
                        .map(id => departments.find(d => d.id === id)?.name)
                        .filter(Boolean);
                      const userNames = group.user_ids
                        .map(id => users.find(u => u.id === id)?.name)
                        .filter(Boolean);
                      const groupNames = [...deptNames, ...userNames];
                      return (
                        <li key={idx}>
                          {groupNames.join(', ')}: {group.tokens.toLocaleString()} tokens
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )} */}
            </div>
            <div className="flex justify-between items-center mb-4">
              {isRenaming ? (
                <div className="flex">
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="border rounded px-2 py-1 mr-2"
                    placeholder="New conversation title"
                  />
                  <button
                    onClick={handleRenameConversation}
                    className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                  >
                    Save
                  </button>
                </div>
              ) : (
                <h2 className="text-xl font-semibold">{selectedConversation.title || 'Untitled Conversation'}</h2>
              )}
              <button
                onClick={() => setIsRenaming(!isRenaming)}
                className="bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
              >
                {isRenaming ? 'Cancel' : 'Rename'}
              </button>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4 h-[calc(100vh-200px)] flex flex-col">
              <div className="flex-grow overflow-y-auto mb-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`mb-2 p-2 rounded max-w-[60%] ${
                      message.is_user_message
                        ? 'ml-auto bg-blue-100'
                        : 'mr-auto bg-gray-100'
                    }`}
                  >
                    {message.content === 'Loading...' ? (
                      <div className="animate-pulse">Loading...</div>
                    ) : (
                      <>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content}
                        </ReactMarkdown>
                        {!message.is_user_message && message.metadata && (
                          <div className="mt-2 text-xs text-gray-500">
                            <div>Cost: ${message.metadata.cost.toFixed(4)}</div>
                            <div>Input tokens: {message.metadata.input_tokens.toLocaleString()}</div>
                            {showAgentDetails && message.metadata.sub_agent_details && (
                              <div className="mt-2">
                                <div className="font-semibold">Agent Details:</div>
                                {message.metadata.sub_agent_details.map((detail, idx) => (
                                  <div key={idx} className="mt-1">
                                    <div className="font-medium">{detail.group_name}:</div>
                                    <div className="text-gray-600">{detail.response}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
              <form onSubmit={handleSendMessage} className="flex flex-col">
                <div className="flex mb-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="flex-grow mr-2 p-2 border rounded"
                    placeholder="Type your message..."
                  />
                  <button
                    type="submit"
                    className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
                  >
                    Send
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPresetQuestions(!showPresetQuestions)}
                  className="bg-gray-200 text-gray-700 py-2 px-4 rounded hover:bg-gray-300"
                >
                  {showPresetQuestions ? 'Hide Preset Questions' : 'Show Preset Questions'}
                </button>
                {showPresetQuestions && (
                  <div className="mt-2 max-h-40 overflow-y-auto">
                    {presetQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => handlePresetQuestion(question)}
                        className="block w-full text-left p-2 hover:bg-gray-100 rounded"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                )}
              </form>
            </div>
          </>
        ) : (
          <div className="text-center text-gray-500">
            Select a conversation or start a new one
          </div>
        )}
      </div>
    </div>
  );
}

export default Chat;